/* Expandable feed stats breakdown by type, tag, and source. */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { BarChart3, ChevronDown, ChevronUp, Layers, Tag, Rss, Star } from "lucide-react";

interface BreakdownData {
  totalItems: number;
  typeStats: Array<{ type: string; count: number }>;
  tagStats: Array<{ tag: string; count: number }>;
  sourceStats: Array<{ name: string; count: number; starred: boolean }>;
}

interface FeedBreakdownSectionProps {
  data: BreakdownData;
  excludeReels: boolean;
  onToggleExcludeReels: () => void;
  onTagClick?: (tag: string) => void;
}

const MAX_TAGS = 12;
const MAX_SOURCES = 20;

export function FeedBreakdownSection({ data, excludeReels, onToggleExcludeReels, onTagClick }: FeedBreakdownSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showAllTags, setShowAllTags] = useState(false);
  const [showAllSources, setShowAllSources] = useState(false);

  return (
    <div className="space-y-2">
      <Button
        variant="ghost"
        size="sm"
        className="h-7 gap-1.5 px-2 text-xs text-muted-foreground hover:text-foreground"
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <BarChart3 className="size-3.5" />
        Feed Breakdown
        {isExpanded ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
      </Button>

      {isExpanded && (
        <div className="space-y-4 rounded-lg border border-border bg-muted/30 p-3 animate-in fade-in slide-in-from-top-1 duration-200">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {data.totalItems} items total
            </span>
            <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
              <Switch
                checked={excludeReels}
                onCheckedChange={onToggleExcludeReels}
                className="scale-75 origin-right"
              />
              Exclude reels
            </label>
          </div>

          {/* By Type */}
          {data.typeStats.length > 0 && (
            <StatGroup icon={Layers} label="By Type">
              {data.typeStats.map((stat) => (
                <StatPill key={stat.type} label={stat.type} count={stat.count} />
              ))}
            </StatGroup>
          )}

          {/* By Tag */}
          {data.tagStats.length > 0 && (
            <StatGroup icon={Tag} label="By Tag">
              {(showAllTags ? data.tagStats : data.tagStats.slice(0, MAX_TAGS)).map((stat) => (
                <StatPill key={stat.tag} label={stat.tag} count={stat.count} onClick={onTagClick ? () => onTagClick(stat.tag) : undefined} />
              ))}
              {data.tagStats.length > MAX_TAGS && (
                <button
                  className="text-[11px] text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                  onClick={() => setShowAllTags((prev) => !prev)}
                >
                  {showAllTags ? "Show less" : `+${data.tagStats.length - MAX_TAGS} more`}
                </button>
              )}
            </StatGroup>
          )}

          {/* By Source */}
          {data.sourceStats.length > 0 && (
            <StatGroup icon={Rss} label="By Source">
              {(showAllSources ? data.sourceStats : data.sourceStats.slice(0, MAX_SOURCES)).map((stat) => (
                <StatPill
                  key={stat.name}
                  label={stat.name}
                  count={stat.count}
                  icon={stat.starred ? <Star className="size-2.5 fill-current text-primary" /> : undefined}
                />
              ))}
              {data.sourceStats.length > MAX_SOURCES && (
                <button
                  className="text-[11px] text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
                  onClick={() => setShowAllSources((prev) => !prev)}
                >
                  {showAllSources ? "Show less" : `+${data.sourceStats.length - MAX_SOURCES} more`}
                </button>
              )}
            </StatGroup>
          )}
        </div>
      )}
    </div>
  );
}

function StatGroup({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
        <Icon className="size-3" />
        {label}
      </div>
      <div className="flex flex-wrap gap-1.5">{children}</div>
    </div>
  );
}

function StatPill({
  label,
  count,
  icon,
  onClick,
}: {
  label: string;
  count: number;
  icon?: React.ReactNode;
  onClick?: () => void;
}) {
  const className = `inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground${onClick ? " cursor-pointer hover:bg-accent transition-colors" : ""}`;
  if (onClick) {
    return (
      <button type="button" className={className} onClick={onClick}>
        {icon}
        <span className="capitalize">{label}</span>
        <span className="font-medium text-foreground">{count}</span>
      </button>
    );
  }
  return (
    <span className={className}>
      {icon}
      <span className="capitalize">{label}</span>
      <span className="font-medium text-foreground">{count}</span>
    </span>
  );
}
